import React, { useMemo } from "react";
import { HiOutlineX } from "react-icons/hi";
import { classNames } from "Utils/utils";

const titleCase = (value) =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const FILTER_LABELS = {
  status: (v) => titleCase(v),
  date: (v) => String(v),
  date_from: (v) => `From ${v}`,
  date_to: (v) => `To ${v}`,
  scope: (v) => {
    if (v === "all_workers") return "All workers";
    if (v === "person") return "Focused person";
    if (v === "org") return "Organisation-wide";
    return titleCase(v);
  },
  entity: (v) => titleCase(v),
  person_table: (v) => {
    if (v === "employee_profiles" || v === "worker") return "Worker focus";
    if (v === "participants" || v === "participant") return "Participant focus";
    return null;
  },
};

/** Meaningful continuity chips — hide bare topic/entity noise. */
const CHIP_ORDER = ["status", "date", "date_from", "date_to", "scope", "person_table"];

/**
 * Non-PII sticky filter chips from conversation metadata / SSE complete.
 * Shows what Vertex360 Copilot will reuse on the next follow-up.
 */
const ActiveFiltersBar = ({
  activeFilters = null,
  // kept for callers; unused now that entity-only bars are hidden
  contextSummary: _contextSummary = null,
  onClear,
  disabled = false,
}) => {
  const chips = useMemo(() => {
    const filters =
      activeFilters && typeof activeFilters === "object" ? activeFilters : null;
    if (!filters || !Object.keys(filters).length) return [];

    const out = [];
    for (const key of CHIP_ORDER) {
      if (filters[key] == null || filters[key] === "") continue;
      // Prefer single-day chip over range endpoints when both exist.
      if (key === "date_from" || key === "date_to") {
        if (filters.date) continue;
      }
      const formatter = FILTER_LABELS[key];
      if (!formatter) continue;
      const label = formatter(filters[key]);
      if (!label) continue;
      out.push({ key, label });
    }
    return out;
  }, [activeFilters]);

  // Don't show a bar for entity-only metadata (e.g. just "shifts") — too noisy.
  if (!chips.length) return null;

  return (
    <div
      className="mb-2 flex flex-wrap items-center gap-1.5 px-0.5"
      aria-label="Active Vertex360 Copilot filters"
    >
      <span
        className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400"
        title="Vertex360 Copilot will reuse these on your next question"
      >
        Remembering
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={classNames(
            "inline-flex max-w-full items-center rounded-full border px-2 py-0.5",
            "border-[#C7D7F5] bg-[#ECF0FD] text-[11px] font-medium text-[#293F6A]"
          )}
        >
          <span className="truncate">{chip.label}</span>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        disabled={disabled || !onClear}
        className={classNames(
          "inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5",
          "border-slate-200 bg-white text-[11px] font-semibold text-slate-600",
          "transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
        aria-label="Clear Vertex360 Copilot filters"
      >
        <HiOutlineX className="h-3 w-3" />
        Clear
      </button>
    </div>
  );
};

export default ActiveFiltersBar;
